<template>
  <div>
    <Header />

    <v-container>
      <v-card
        v-if="mode === 'normal'"
        :loading="loading"
        class="mx-auto"
        max-width="500"
      >
        <v-card-title
          class="pa-8 font-weight-bold primary--text"
        >
          <h2>
            Register
          </h2>
        </v-card-title>
        <v-divider />
        <v-card-text
          class="px-8"
        >
          <v-form ref="form">
            <v-text-field
              v-model="form.email"
              label="Email"
              :rules="rules.email"
              readonly
              disabled
            />
            <v-row>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-text-field
                  v-model="form.first_name"
                  label="First Name"
                  :rules="rules.name"
                  :disabled="loading"
                />
              </v-col>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-text-field
                  v-model="form.last_name"
                  label="Last Name"
                  :rules="rules.name"
                  :disabled="loading"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-text-field
                  v-model="form.new_password"
                  label="Password"
                  :rules="rules.new_password"
                  counter
                  :append-icon="field.show1 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="field.show1 ? 'text' : 'password'"
                  :disabled="loading"
                  @click:append="field.show1 = !field.show1"
                />
              </v-col>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-text-field
                  v-model="passwordConfirm"
                  label="Confirm Password"
                  :rules="rules.passwordConfirm"
                  counter
                  :append-icon="field.show2 ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="field.show2 ? 'text' : 'password'"
                  :disabled="loading"
                  @click:append="field.show2 = !field.show2"
                />
              </v-col>
            </v-row>
            <v-autocomplete
              v-model="form.time_zone"
              label="Time Zone"
              :items="timezoneList"
              item-text="display"
              item-value="value"
              :disabled="loading"
              :rules="rules.timezone"
            />
          </v-form>
        </v-card-text>
        <v-card-actions
          class="flex-wrap px-6 pb-6"
        >
          <div style="width: 100%; text-align: center;">
            <v-checkbox
              v-model="form.is_tos_agreed"
              label="Agree with term"
              :disabled="loading"
            >
              <template #label>
                <div class="d-flex flex-wrap align-center">
                  I have read and agree to Global Coach Group's
                  <v-btn
                    class="py-0 px-0 mx-1 body-1 text-decoration-underline"
                    color="primary"
                    height="18"
                    text
                    :disabled="loading"
                    @click.stop="openLink('tos')"
                  >
                    Terms of Service
                  </v-btn>
                  and
                  <v-btn
                    class="py-0 px-0 mx-1 body-1 text-decoration-underline"
                    color="primary"
                    height="18"
                    text
                    :disabled="loading"
                    @click.stop="openLink('privacy-policy')"
                  >
                    Privacy Policy
                  </v-btn>
                </div>
              </template>
            </v-checkbox>
          </div>
          <div style="width: 100%; text-align: center;">
            <v-btn
              :loading="loading"
              color="primary lighten-2"
              :disabled="!form.is_tos_agreed"
              rounded
              large
              @click="submit"
            >
              Next
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>

      <RegisterError
        v-else-if="mode === 'error'"
      />
      <RegisterSuccess
        v-else-if="mode === 'success'"
        :redirect-to="redirectTo"
      />
    </v-container>
  </div>
</template>

<script>
import accountApi from '@/api/account/account';
import Header from '@/views/components/Header.vue';
import RegisterError from './widget/RegisterError.vue';
import RegisterSuccess from './widget/RegisterSuccess.vue';

export default {
  name: 'UserRegistration',

  components: {
    Header,
    RegisterError,
    RegisterSuccess,
  },

  data() {
    return {
      mode: 'normal',
      loading: false,
      field: {
        show1: false,
        show2: false,
      },
      timezoneList: [],
      form: {
        email: null,
        new_password: null,
        first_name: null,
        last_name: null,
        time_zone: null,
        is_tos_agreed: false,
      },
      passwordConfirm: null,
      rules: {
        name: [
          (v) => !!v || 'Name is required.',
        ],
        email: [
          (v) => !!v || 'E-mail is required.',
          (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid',
        ],
        new_password: [
          (v) => !!v || 'Password is required.',
          (v) => {
            const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!";#$%&'()*+,./:<=>?@[^_`{|}\-\]~])[A-Za-z\d!";#$%&'()*+,./:<=>?@[^_`{|}\-\]~]{8,}$/;
            return regex.test(v) || 'Password must include one upper character[A-Z], one integer[0-9], and one special character.';
          },
        ],
        passwordConfirm: [
          (v) => !!v || 'Confirm password is required.',
          (v) => (v === this.form.new_password) || 'Password does not match',
        ],
        timezone: [
          (v) => !!v || 'Time zone is required.',
        ],
      },
      redirectTo: null,
    };
  },

  created() {
    this.doVerify();
  },

  methods: {
    async doVerify() {
      try {
        this.loading = true;
        const params = {
          uid64: this.$route.params.uid64,
          token: this.$route.params.token,
        };
        const response = await accountApi.inviteUserVerify(params);
        this.form.email = response.email;
        this.getTimezone();
      } catch (err) {
        console.log(err);
        this.mode = 'error';
      } finally {
        this.loading = false;
      }
    },

    async getTimezone() {
      try {
        this.timezoneList = await accountApi.getTimezone();
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg);
      }
    },

    openLink(version) {
      if (version) {
        window.open(`https://globalcoachgroup.com/${version}/`, '_blank');
      } else {
        window.open('https://globalcoachgroup.com/tos/', '_blank');
      }
    },

    async submit() {
      if (this.$refs.form.validate() && this.form.is_tos_agreed) {
        try {
          this.loading = true;
          const params = {
            uid64: this.$route.params.uid64,
            token: this.$route.params.token,
            ...this.form,
          };
          const response = await accountApi.inviteUserRegister(params);
          this.mode = 'success';
          if (response.redirect_to) this.redirectTo = response.redirect_to;
        } catch (err) {
          console.log(err);
          this.mode = 'error';
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>
